
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, ExternalLink, Upload, Loader2, Download } from "lucide-react";
import { FullLicenseText } from "@/components/licensing/FullLicenseText";
import { LicenseVariables, MultiLingualContent, getLocalizedContent } from "@/types/licensing";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

interface Download {
  id: string;
  clipId: string;
  clipName: string;
  thumbnailUrl: string;
  licenseId: string;
  licenseName: string;
  downloadDate: string;
  exactDownloadTimestamp: string;
  licenseVariables: LicenseVariables;
  permissions?: MultiLingualContent[] | string[];
  restrictions?: MultiLingualContent[] | string[];
}

const DownloadHistory: React.FC = () => {
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<{
    download: Download;
    variables: LicenseVariables;
    permissions?: MultiLingualContent[] | string[];
    restrictions?: MultiLingualContent[] | string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isMobile = useIsMobile();

  useEffect(() => {
    async function fetchDownloads() {
      if (!user) return;
      
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('downloads')
          .select('*')
          .eq('user_id', user.id)
          .order('downloaded_at', { ascending: false });
        
        if (error) {
          console.error("Error fetching downloads:", error);
          toast.error("Failed to load download history");
          return;
        }
        
        const formattedDownloads: Download[] = [];
        
        for (const item of data) {
          const { data: clipData, error: clipError } = await supabase
            .from('clips')
            .select('license_id, title, thumbnail')
            .eq('id', item.clip_id)
            .maybeSingle();
          
          if (clipError) {
            console.error("Error fetching clip:", clipError);
            continue;
          }
          
          let licenseData = {
            name: "Standard License",
            rights_holder: "Pathé",
            mentions: ["pathefilms", "recut.art"],
            hashtags: ["recut"],
            permissions: [],
            restrictions: []
          };
          
          if (clipData?.license_id) {
            const { data: license, error: licenseError } = await supabase
              .from('licenses')
              .select('*')
              .eq('id', clipData.license_id)
              .maybeSingle();
              
            if (!licenseError && license) {
              licenseData = {
                name: license.name,
                rights_holder: license.rights_holder,
                mentions: license.mentions || [],
                hashtags: license.hashtags || [],
                permissions: license.permissions || [],
                restrictions: license.restrictions || []
              };
            }
          }
          
          formattedDownloads.push({
            id: item.id,
            clipId: item.clip_id,
            clipName: item.clip_title,
            thumbnailUrl: clipData?.thumbnail || "/placeholder.svg",
            licenseId: clipData?.license_id || "default",
            licenseName: licenseData.name,
            downloadDate: new Date(item.downloaded_at).toLocaleDateString(
              language === 'en' ? 'en-US' : 'fr-FR',
              { year: 'numeric', month: 'long', day: 'numeric' }
            ),
            exactDownloadTimestamp: item.downloaded_at,
            licenseVariables: {
              rightsholder: licenseData.rights_holder,
              mentions: licenseData.mentions,
              hashtags: licenseData.hashtags
            },
            permissions: licenseData.permissions,
            restrictions: licenseData.restrictions
          });
        }
        
        setDownloads(formattedDownloads);
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchDownloads();
  }, [user, language]);

  const openLicenseDetails = (download: Download) => {
    setSelectedLicense({
      download,
      variables: download.licenseVariables,
      permissions: download.permissions,
      restrictions: download.restrictions
    });
    setLicenseDialogOpen(true);
  };

  const handleCreateDerivative = (clipId: string) => {
    navigate(`/create-derivative?clipId=${clipId}`);
  };

  const convertToMultiLingual = (items: any[] = []): MultiLingualContent[] => {
    if (items.length === 0) return [];
    
    if (typeof items[0] === 'object' && 'text' in items[0] && 'language' in items[0]) {
      return items as MultiLingualContent[];
    }
    
    if (typeof items[0] === 'string' && items[0].includes('|')) {
      return items.map((item: string) => {
        const [text, lang] = item.split('|');
        return { text, language: lang as 'en' | 'fr' };
      });
    }
    
    if (typeof items[0] === 'string') {
      return items.map((text: string) => ({ text, language: 'en' }));
    }
    
    return [];
  };

  const downloadLicenseAsPDF = async () => {
    if (!selectedLicense || !user) return;
    
    try {
      setIsGeneratingPdf(true);
      console.log("Starting PDF generation process");
      
      // Set up PDF document with A4 dimensions
      const pdf = new jsPDF({
        format: 'a4',
        unit: 'mm',
        orientation: 'portrait'
      });
      
      // Convert permissions and restrictions to localized content
      const permissions = selectedLicense.permissions 
        ? getLocalizedContent(convertToMultiLingual(selectedLicense.permissions), language) 
        : [];
      const restrictions = selectedLicense.restrictions 
        ? getLocalizedContent(convertToMultiLingual(selectedLicense.restrictions), language) 
        : [];
      
      // Create a container for PDF content
      const pdfContentDiv = document.createElement('div');
      pdfContentDiv.id = 'license-pdf-content';
      pdfContentDiv.style.width = '794px'; // A4 width at 96 DPI
      pdfContentDiv.style.padding = '40px';
      pdfContentDiv.style.backgroundColor = 'white';
      pdfContentDiv.style.color = 'black';
      pdfContentDiv.style.fontFamily = 'Arial, sans-serif';
      pdfContentDiv.style.position = 'absolute';
      pdfContentDiv.style.left = '-9999px';
      pdfContentDiv.style.fontSize = '12px';
      document.body.appendChild(pdfContentDiv);
      
      // Create the header with basic HTML
      const headerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 24px; margin-bottom: 10px; font-weight: bold;">${language === 'en' ? 'License Certificate' : 'Certificat de Licence'}</h1>
          <p style="font-size: 14px; color: #333;">${language === 'en' ? 'This document certifies the license terms for the downloaded content' : 'Ce document certifie les conditions de licence pour le contenu téléchargé'}</p>
        </div>
        <div style="margin-bottom: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 16px;">
          <p style="margin-bottom: 10px; font-size: 14px;"><strong>${language === 'en' ? 'Clip Name' : 'Nom du clip'}: </strong>${selectedLicense.download.clipName}</p>
          <p style="margin-bottom: 10px; font-size: 14px;"><strong>${language === 'en' ? 'Download Date' : 'Date de téléchargement'}: </strong>${new Date(selectedLicense.download.exactDownloadTimestamp).toLocaleString(language === 'en' ? 'en-US' : 'fr-FR')}</p>
          <p style="margin-bottom: 10px; font-size: 14px;"><strong>${language === 'en' ? 'Creator Name' : 'Nom du créateur'}: </strong>${user?.user_metadata?.full_name || ''}</p>
          <p style="margin-bottom: 10px; font-size: 14px;"><strong>${language === 'en' ? 'License Type' : 'Type de licence'}: </strong>${selectedLicense.download.licenseName}</p>
        </div>
      `;
      pdfContentDiv.innerHTML = headerHTML;
      
      // Create the license content summary section
      const licenseContentHTML = `
        <div style="margin-bottom: 30px;">
          <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold;">${language === 'en' ? 'License Summary' : 'Synthèse des conditions'}</h2>
          
          <div style="margin-bottom: 20px;">
            <p style="font-size: 14px; margin-bottom: 10px;"><strong>${language === 'en' ? 'Rights holder' : 'Ayant droit'}: </strong>${selectedLicense.variables.rightsholder || "N/A"}</p>
          </div>
          
          ${selectedLicense.variables.mentions && selectedLicense.variables.mentions.length > 0 ? `
            <div style="margin-bottom: 20px;">
              <p style="font-size: 12px; text-transform: uppercase; color: #4b5563; margin-bottom: 8px; font-weight: bold;">${language === 'en' ? 'Required Mentions' : 'MENTIONS REQUISES'}:</p>
              <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px;">
                ${selectedLicense.variables.mentions.map(mention => `
                  <span style="border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 8px; font-size: 13px;">@${mention}</span>
                `).join('')}
              </div>
            </div>
          ` : ''}
          
          ${selectedLicense.variables.hashtags && selectedLicense.variables.hashtags.length > 0 ? `
            <div style="margin-bottom: 20px;">
              <p style="font-size: 12px; text-transform: uppercase; color: #4b5563; margin-bottom: 8px; font-weight: bold;">${language === 'en' ? 'Required Hashtags' : 'HASHTAGS REQUIS'}:</p>
              <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px;">
                ${selectedLicense.variables.hashtags.map(hashtag => `
                  <span style="border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 8px; font-size: 13px;">#${hashtag}</span>
                `).join('')}
              </div>
            </div>
          ` : ''}
          
          ${permissions.length > 0 ? `
            <div style="margin-bottom: 20px;">
              <p style="font-size: 12px; text-transform: uppercase; color: #4b5563; margin-bottom: 8px; font-weight: bold;">${language === 'en' ? 'Permissions' : 'Autorisations'}:</p>
              <ul style="list-style-type: disc; margin-left: 20px; line-height: 1.5; font-size: 13px;">
                ${permissions.map(permission => `<li>${permission}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          
          ${restrictions.length > 0 ? `
            <div style="margin-bottom: 20px;">
              <p style="font-size: 12px; text-transform: uppercase; color: #4b5563; margin-bottom: 8px; font-weight: bold;">${language === 'en' ? 'Restrictions' : 'Restrictions'}:</p>
              <ul style="list-style-type: disc; margin-left: 20px; line-height: 1.5; font-size: 13px;">
                ${restrictions.map(restriction => `<li>${restriction}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
        </div>
      `;
      
      // Add the license content to the PDF container
      const licenseDiv = document.createElement('div');
      licenseDiv.innerHTML = licenseContentHTML;
      pdfContentDiv.appendChild(licenseDiv);
      
      // Add the full license terms with all the content
      const fullTermsHTML = `
        <div style="margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold;">${language === 'en' ? 'Full License Terms' : 'Conditions Générales de Licence'}</h2>
          <div style="font-size: 12px; line-height: 1.4;">
            ${language === 'en' ? `
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Conditions Générales d'utilisation ReCut</h4>
              <p style="margin-bottom: 8px;">Effective Date: June 24, 2024</p>
              <p style="font-weight: bold; margin-bottom: 8px;">PLEASE FIND BELOW THE GENERAL TERMS AND CONDITIONS OF USE AND LICENSE GOVERNING:</p>
              <p style="margin-bottom: 8px;">Access to the Recut platform</p>
              <p style="margin-bottom: 8px;">Use of Recut platform functionalities, viewing, downloading, or using content made available on the Recut platform, the rights to which are held by Recut's rights holder partners.</p>
              <p style="margin-bottom: 8px;">By accessing the Recut platform and using or downloading the offered content, you accept all the terms of this agreement and become a User, within the meaning of this agreement ("the Service").</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 1 - Service Access License</h4>
              <p style="margin-bottom: 8px;">You are granted a limited, non-exclusive, non-transferable, non-sublicensable, and personal license to access and use the Service, subject to your compliance with these terms and conditions. If any content or other material owned by, controlled by, or licensed is distributed or made available to you in connection with your use of the Service, we hereby grant you a personal, non-commercial, non-assignable, non-sublicensable, non-transferable, and non-exclusive license to access, view, display, and use that content, for the sole purpose of enabling you to use the Service, as defined by these terms and conditions.</p>
              <p style="margin-bottom: 8px;">Recut may remove or withdraw or restrict the use of any content, at any time, in its sole and absolute discretion.</p>
              <p style="margin-bottom: 8px;">By using the Service, you agree not to violate any law, contract, intellectual property right, or other third-party right, and that you are solely responsible for your access to or use of the Service.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 2 - User's Use of Content</h4>
              <p style="margin-bottom: 8px;">The various contents offered may be used by the User, in accordance with the access conditions and rights granted for each specific content and subject to compliance with the conditions of this license.</p>
              <p style="margin-bottom: 8px;">The term "use" refers to the act, for the user, of downloading, copying, reproducing, modifying, editing, synchronizing, broadcasting, or publishing the content offered by Recut, on the user's personal social networks, in order to create User Generated Contents "UGC".</p>
              <p style="margin-bottom: 8px;">The proposed contents may be used by the User, for the creation of UGC, which may be broadcast for the legal duration of copyright, for the entire world and for exploitation on the User's personal or official social network pages only (namely TikTok, Instagram, Facebook or Youtube), to the exclusion of any other medium.</p>
              <p style="margin-bottom: 8px;">The license granted for these contents to the User is non-exclusive and non-transferable. Recut reserves the right to grant licenses for the same content to other users/customers. The User may not grant sublicenses to third parties. Use is strictly reserved for the User.</p>
              <p style="margin-bottom: 8px;">The User undertakes, for each creation of UGC, to publish the following elements, concurrently with the posting of the content:</p>
              
              <div style="margin-left: 12px; margin-bottom: 12px; margin-top: 12px;">
                <p style="font-weight: 600; margin-bottom: 6px;">Required Mentions:</p>
                <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
                  ${selectedLicense.variables.mentions?.map(mention => `<span style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">@${mention}</span>`).join('') || ''}
                </div>
                
                <p style="font-weight: 600; margin-bottom: 6px;">Required Hashtags:</p>
                <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
                  ${selectedLicense.variables.hashtags?.map(hashtag => `<span style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">#${hashtag}</span>`).join('') || ''}
                </div>
              </div>

              <p style="margin-bottom: 8px;">You represent and warrant that you have, or have obtained, all rights, licenses, consents, permissions, power and/or authority necessary to exploit any UGC containing content used through the Service.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 3 - Content Usage Restrictions</h4>
              <p style="margin-bottom: 8px;">No illegal use will be accepted. The User agrees to use the contents as follows:</p>
              <ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 12px;">
                <li style="margin-bottom: 5px;">No illegal use. You may not use content for pedocriminal, pornographic purposes, to promote suicide or self-harm, to create "deepfakes", to incite violence or hatred, to trivialize criminal behavior, to train an artificial intelligence platform, in prompts or more generally, in violation of any regulations in force or any professional code.</li>
                <li style="margin-bottom: 5px;">No modification of content outside of context. The content may be cropped or otherwise modified for technical quality reasons, or associated with original content from the User ("UGC") provided that the editorial integrity of the content is not compromised. The integrity of the content shall in no case be altered.</li>
                <li style="margin-bottom: 5px;">No use as a standalone file. You may not use the content in a way that allows others to download, extract, or redistribute the content as a standalone file (i.e., only the content file itself, separate from the project or end use) or use content in connection with an immutable digital file intended for sale or to be distributed in another way (for example, a non-fungible token - NFT).</li>
                <li style="margin-bottom: 5px;">No commercial use. You may not use the content in association with trademarks, advertorials, sponsorship, advertising, gambling, betting or gaming, or merchandising or for promotional purposes for the promotion of third-party products and services.</li>
                <li style="margin-bottom: 5px;">No false claim of authorship. You may not claim that you are the original creator of the content.</li>
                <li style="margin-bottom: 5px;">No use at the end of the license period. You may not reuse content that is no longer accessible on Recut, nor republish all or part of a UGC created on the basis of content.</li>
                <li style="margin-bottom: 5px;">No sensitive or controversial use. You may not use the contents to create UGC that are openly controversial, critical, sensitive, or that violate the dignity of an individual or group of people or public decency.</li>
                <li style="margin-bottom: 5px;">No use in a trademark or logo. You may not use the content (in whole or in part) as a distinctive element of a trademark, design mark, trade name, business name, service mark or logo. Furthermore, you do not have the right to register such content (in whole or in part) as a trademark or copyright.</li>
                <li style="margin-bottom: 5px;">No use in Machine Learning, Artificial Intelligence, or biometric technology. You may not use the content (including caption information, keywords, or other metadata associated with the content) for machine learning and/or artificial intelligence purposes, or for any technology designed or intended for the identification of natural persons or allow third parties to access or use this information associated with the content.</li>
              </ul>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 4 - Ownership</h4>
              <p style="margin-bottom: 8px;">The Service, including the graphics of the Recut platform, all content, information, and other elements and all audiovisual works, designs, texts, graphics, images, data, software, sound files, other files, and the selection and arrangement thereof are the exclusive property of Recut and its partners, licensors, or users. We and our licensors and users, as applicable, reserve all rights in connection with the Service and its content, including, without limitation, the exclusive right to create derivative works.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 5 - User Account</h4>
              <p style="margin-bottom: 8px;">You represent that you are of legal age in the jurisdiction in which you reside, to enter into a contract (and in any case, are over thirteen (13) years of age) and that you have full legal authority to enter into and perform this agreement and give these representations and warranties. If you are a legal entity, you represent that the person acting on your behalf is authorized to do so and to bind the legal entity.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 6 - Liability</h4>
              <p style="margin-bottom: 8px;">The Service, access to the Platform and content requires internet access. Recut undertakes to use its best efforts to ensure permanent access to the Service, as well as its proper functioning. Recut reserves the right to interrupt access to the Service for maintenance reasons. Recut cannot be held responsible for the unavailability of the Service, particularly in the event of: (i) evolutionary or corrective maintenance, including when the maintenance intervention is carried out by third parties; (ii) occurrence of a force majeure event as defined by the courts, (iii) failure of the internet network or (iv) the User's equipment.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 7 - Applicable Law and Jurisdiction</h4>
              <p style="margin-bottom: 8px;">These terms and conditions are drawn up in accordance with French law. Any dispute relating to access to the Service, content available on Recut and/or the interpretation of these terms and conditions will be subject to the exclusive jurisdiction of the competent courts of Paris.</p>
            ` : `
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Conditions Générales d'utilisation ReCut</h4>
              <p style="margin-bottom: 8px;">Date d'entrée en vigueur : 24 juin 2024</p>
              <p style="font-weight: bold; margin-bottom: 8px;">VEUILLEZ TROUVER CI-DESSOUS LES CONDITIONS GÉNÉRALES D'UTILISATION ET DE LICENCE RÉGISSANT :</p>
              <p style="margin-bottom: 8px;">L'accès à la plateforme Recut</p>
              <p style="margin-bottom: 8px;">L'usage des fonctionnalités de la plateforme Recut, le visionnage, le téléchargement ou l'utilisation des contenus mis à disposition sur la plateforme Recut, dont les droits sont détenus par les ayants droit partenaires de Recut.</p>
              <p style="margin-bottom: 8px;">En accédant à la plateforme Recut et en utilisant ou téléchargeant le contenu proposé, vous acceptez l'ensemble des termes du présent contrat et devenez Utilisateur, au sens du présent contrat (« le Service »).</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 1 – Licence d'accès au Service</h4>
              <p style="margin-bottom: 8px;">Vous bénéficiez d'une licence limitée, non exclusive, non transférable, non sous-licenciable et personnelle pour accéder et utiliser le Service, sous réserve que cette licence soit soumise à votre conformité à ces conditions générales. Si un contenu ou autre matériel détenu par, contrôlé par ou concédé sous licence est distribué ou mis à votre disposition dans le cadre de votre utilisation du Service, nous vous accordons par la présente une licence personnelle, non commerciale, non assignable, non sous-licenciable, non transférable et non exclusive pour accéder, consulter, afficher et utiliser ce contenu, dans le seul but de vous permettre d'utiliser le Service, tel que défini par ces conditions générales.</p>
              <p style="margin-bottom: 8px;">Recut peut supprimer ou retirer ou restreindre l'usage de tout contenu, à tout moment, à sa seule et absolue discrétion.</p>
              <p style="margin-bottom: 8px;">En utilisant le Service, vous acceptez de ne pas violer de loi, contrat, droit de propriété intellectuelle ou autre droit de tiers, et que vous êtes seul responsable de votre accès ou votre utilisation du Service.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 2 - L'utilisation du contenu par l'Utilisateur</h4>
              <p style="margin-bottom: 8px;">Les différents contenus proposés peuvent être utilisés, par l'Utilisateur, conformément aux conditions d'accès et droits accordés pour chaque contenu spécifique et sous réserve du respect des conditions de la présente licence.</p>
              <p style="margin-bottom: 8px;">Le terme « utiliser » désigne le fait, pour l'utilisateur, de télécharger, copier, reproduire, modifier, éditer, synchroniser, diffuser ou publier le contenu proposé par Recut, sur les réseaux sociaux personnels de l'utilisateur, afin de créer des User Generated Contents « UGC ».</p>
              <p style="margin-bottom: 8px;">Les contenus proposés peuvent être utilisés par l'Utilisateur, pour la création d'UGC, qui pourront être diffusés pour la durée légale des droits d'auteur, pour le monde entier et pour une exploitation sur les pages personnelles ou officielles des réseaux sociaux de l'Utilisateur uniquement (à savoir TikTok, Instagram, Facebook ou Youtube), à l'exclusion de tout autre support.</p>
              <p style="margin-bottom: 8px;">La licence accordée pour ces contenus à l'Utilisateur est non exclusive et non transférable. Recut se réserve le droit d'accorder des licences portant sur le même contenu à d'autres utilisateurs/clients. L'Utilisateur ne peut consentir de sous-licence à des tiers. L'usage est strictement réservé à l'Utilisateur.</p>
              <p style="margin-bottom: 8px;">L'Utilisateur s'engage, pour chaque création d'UGC, de publier les éléments suivants, concomitamment à la mise en ligne du contenu :</p>
              
              <div style="margin-left: 12px; margin-bottom: 12px; margin-top: 12px;">
                <p style="font-weight: 600; margin-bottom: 6px;">Mentions Requises :</p>
                <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
                  ${selectedLicense.variables.mentions?.map(mention => `<span style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">@${mention}</span>`).join('') || ''}
                </div>
                
                <p style="font-weight: 600; margin-bottom: 6px;">Hashtags Requis :</p>
                <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
                  ${selectedLicense.variables.hashtags?.map(hashtag => `<span style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px;">#${hashtag}</span>`).join('') || ''}
                </div>
              </div>
              
              <p style="margin-bottom: 8px;">Vous déclarez et garantissez que vous avez, ou avez obtenu, tous les droits, licences, consentements, permissions, pouvoir et/ou autorité nécessaires pour exploiter tout UGC contenant un contenu utilisé par le biais du Service.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 3 – Restrictions d'utilisation des contenus</h4>
              <p style="margin-bottom: 8px;">Aucune utilisation illégale ne sera acceptée. L'Utilisateur s'engage à un usage des contenus comme suit :</p>
              <ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 12px;">
                <li style="margin-bottom: 5px;">Pas d'utilisation illégale. Vous ne pouvez pas utiliser de contenu à des fins pédocriminelles, pornographiques, de promotion du suicide ou de l'automutilation, pour créer des « deepfake », pour inciter à la violence ou la haine, pour banaliser des comportements délictuels, pour entraîner une plateforme d'intelligence artificielle, dans des prompts ou plus généralement, en violation de toute réglementation en vigueur ou de tout code professionnel.</li>
                <li style="margin-bottom: 5px;">Pas de modification de contenu en dehors du contexte. Le contenu peut être recadré ou autrement modifié pour des raisons de qualité technique, ou associé à des contenus originaux de l'Utilisateur (« UGC ») à condition que l'intégrité éditoriale du contenu ne soit pas compromise. L'intégrité du contenu devra en aucun cas être altérée.</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation en tant que fichier autonome. Vous ne pouvez pas utiliser le contenu de façon permettant à d'autres de télécharger, extraire ou redistribuer le contenu en tant que fichier autonome (c'est-à-dire uniquement le fichier du contenu lui-même, séparé du projet ou de l'utilisation finale) ni utiliser de contenu en relation avec un fichier numérique immuable destiné à la vente ou à être distribué d'une autre façon (par exemple un jeton non fongible - NFT).</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation commerciale. Vous ne pouvez pas utiliser le contenu en association avec des marques commerciales, de publireportage, de parrainage, de publicité, de jeux d'argent, paris ou gaming, ou de merchandising ou dans une finalité promotionnelle pour la promotion de produits et services de tiers.</li>
                <li style="margin-bottom: 5px;">Pas de fausse déclaration de paternité d'œuvre. Vous ne pouvez pas déclarer que vous êtes le créateur original du contenu.</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation au terme de la période de licence. Vous ne pouvez pas réutiliser un contenu qui n'est plus accessible sur Recut, ni même republier tout ou partie d'un UGC créé sur la base d'un contenu.</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation sensible ou polémique. Vous ne pouvez pas utiliser les contenus pour créer des UGC ouvertement polémiques, critiques, sensibles ou portant atteinte à la dignité d'un individu ou d'un groupe de personnes ou aux bonnes mœurs.</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation dans une marque ou un logo. Vous ne pouvez pas utiliser le contenu (en tout ou en partie) comme élément distinctif d'une marque, d'un dessin-marque, d'un nom commercial, d'une raison sociale, d'une marque de service ou d'un logo. De plus, vous n'avez pas le droit d'enregistrer un tel contenu (en tout ou en partie) en tant que marque ou copyright.</li>
                <li style="margin-bottom: 5px;">Pas d'utilisation dans Machine Learning, Intelligence Artificielle ou de technologie biométrique. Vous ne pouvez pas utiliser le contenu (y compris les informations de légende, les mots-clés ou autres métadonnées associées au contenu) à des fins d'apprentissage machine et/ou d'intelligence artificielle, ou pour toute technologie conçue ou destinée à l'identification des personnes physiques ni permettre à des tiers d'accéder à ou d'utiliser ces informations associées au contenu.</li>
              </ul>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 4 – Propriété</h4>
              <p style="margin-bottom: 8px;">Le Service, y compris le graphisme de la plateforme Recut, l'ensemble des contenus, informations et autres éléments et tous les œuvres audiovisuelles, designs, textes, graphiques, images, données, logiciels, fichiers sonores, autres fichiers, et la sélection et l'arrangement de ceux-ci sont la propriété exclusive de Recut et de ses partenaires, concédants ou utilisateurs. Nous et nos concédants et utilisateurs, selon le cas, nous réservons tous les droits en relation avec le Service et son contenu, y compris, sans limitation, le droit exclusif de créer des œuvres dérivées.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 5 – Compte Utilisateur</h4>
              <p style="margin-bottom: 8px;">Vous déclarez être majeur dans la juridiction dans laquelle vous résidez, pour conclure un contrat (et en tout état de cause, êtes âgé de plus de treize (13) ans) et que vous avez pleine autorité légale pour conclure et exécuter cet accord et donner ces déclarations et garanties. Si vous êtes une personne morale, vous déclarez que la personne agissant en votre nom est autorisée à le faire et à engager la personne morale.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 6 – Responsabilité</h4>
              <p style="margin-bottom: 8px;">Le Service, l'accès à la Plateforme et aux contenus nécessitent un accès internet. Recut s'engage à fournir ses meilleurs efforts afin d'assurer un accès permanent au Service, ainsi que son bon fonctionnement. Recut se réserve la possibilité d'interrompre l'accès au Service pour des raisons de maintenance. Recut ne peut être tenu responsable de l'indisponibilité du Service, notamment en cas de : (i) maintenance évolutive ou corrective, y compris lorsque l'intervention de maintenance est réalisée par des tiers ; (ii) survenance d'un évènement de force majeure tel que défini par les juridictions, (iii) défaillance du réseau internet ou (iv) de l'équipement de l'Utilisateur.</p>
              
              <h4 style="font-weight: bold; margin-bottom: 8px; margin-top: 12px;">Article 7 – Droit applicable et juridiction</h4>
              <p style="margin-bottom: 8px;">Ces conditions générales sont rédigées conformément au droit français. Tout litige relatif à l'accès au Service, aux contenus disponibles sur Recut et/ou à l'interprétation de ces conditions générales sera soumis à la juridiction exclusive des tribunaux compétents de Paris.</p>
            `}
            
            <p style="margin-top: 20px; font-style: italic; font-size: 11px; text-align: center;">
              ${language === 'en' 
                ? 'This document was generated automatically and serves as an official record of the license terms agreed to at the time of download.'
                : 'Ce document a été généré automatiquement et sert de trace officielle des conditions de licence acceptées au moment du téléchargement.'}
            </p>
          </div>
        </div>
      `;
      
      const fullTermsDiv = document.createElement('div');
      fullTermsDiv.innerHTML = fullTermsHTML;
      pdfContentDiv.appendChild(fullTermsDiv);
      
      console.log("HTML content prepared, starting PDF conversion");
      
      // Generate PDF with more conservative settings
      const options = {
        scale: 1.0, 
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#FFFFFF"
      };
      
      try {
        console.log("Starting html2canvas conversion with options:", options);
        
        const canvas = await html2canvas(pdfContentDiv, options);
        
        console.log("Canvas created, size:", canvas.width, "x", canvas.height);
        
        // More aggressive JPEG compression for smaller file size
        const imgData = canvas.toDataURL('image/jpeg', 0.7);
        
        // Calculate dimensions to fit A4 page
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        let heightLeft = imgHeight;
        let position = 0;
        
        // Add image to PDF first page
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        // Add more pages if needed
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        
        console.log("PDF generated, saving file");
        
        // Save the PDF with a clean filename
        const filename = `license_${selectedLicense.download.clipName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(filename);
        
        console.log("PDF saved successfully:", filename);
        toast.success(language === 'en' ? "License downloaded as PDF" : "Licence téléchargée en PDF");
      } catch (innerError) {
        console.error("Inner error in PDF generation:", innerError);
        throw innerError;
      } finally {
        // Clean up
        if (document.body.contains(pdfContentDiv)) {
          document.body.removeChild(pdfContentDiv);
        }
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(language === 'en' ? "Failed to generate PDF" : "Échec de génération du PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
        <span>Loading downloads...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className={`${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
          <CardTitle>Download History</CardTitle>
          <CardDescription className="text-sm">Clips you've downloaded from the platform</CardDescription>
        </CardHeader>
        <CardContent className={`${isMobile ? 'px-3 py-2' : 'px-6 py-4'}`}>
          {downloads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>You haven't downloaded any clips yet.</p>
            </div>
          ) : isMobile ? (
            <div className="space-y-3">
              {downloads.map((download) => (
                <Card key={download.id} className="overflow-hidden">
                  <div className="p-3 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-sm">{download.clipName}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Downloaded: {download.downloadDate}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openLicenseDetails(download)}
                        className="h-8 text-xs w-full"
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        License Terms
                      </Button>
                      <div className="flex w-full gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild
                          className="h-8 text-xs flex-1"
                        >
                          <a href={`/clip/${download.clipId}`}>
                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                            View Clip
                          </a>
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => handleCreateDerivative(download.clipId)}
                          className="h-8 text-xs flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        >
                          <Upload className="h-3.5 w-3.5 mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clip</TableHead>
                  <TableHead>Download Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {downloads.map((download) => (
                  <TableRow key={download.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <span className="font-medium">{download.clipName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{download.downloadDate}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openLicenseDetails(download)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          License Terms
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href={`/clip/${download.clipId}`} className="flex items-center">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View Clip
                          </a>
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => handleCreateDerivative(download.clipId)}
                          className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Upload Derivative
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={licenseDialogOpen} onOpenChange={setLicenseDialogOpen}>
        <DialogContent className={`max-w-3xl ${isMobile ? 'p-3 max-h-[90vh]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>License Terms & Requirements</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={downloadLicenseAsPDF}
                disabled={isGeneratingPdf}
                className="ml-4"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    {language === 'en' ? 'Generating...' : 'Génération...'}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" />
                    {language === 'en' ? 'Download PDF' : 'Télécharger PDF'}
                  </>
                )}
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className={`${isMobile ? 'max-h-[65vh]' : 'max-h-[70vh]'} overflow-y-auto pr-1`}>
            {selectedLicense && (
              <div className="license-content-for-pdf">
                <FullLicenseText 
                  licenseVariables={selectedLicense.variables}
                  permissions={selectedLicense.permissions ? 
                    getLocalizedContent(convertToMultiLingual(selectedLicense.permissions), language) : 
                    []}
                  restrictions={selectedLicense.restrictions ? 
                    getLocalizedContent(convertToMultiLingual(selectedLicense.restrictions), language) : 
                    []}
                  condensed={true}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DownloadHistory;
